import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {useMediaQuery} from '@mantine/hooks';
import {Pill} from 'src/design/atoms/Pill';
import {Draggable, Droppable} from '@hello-pangea/dnd';
import React, {useMemo} from 'react';
import type {InspectionDispatchInspectorResponse} from 'src/api/ProactApiClient';
import type {DispatchCardMeta} from './DispatchCard';
import type {DispatchView} from '../dispatchCalendar';
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  WEEK_DEFAULT_HOUR,
  dayHours,
  monthCalendarCells,
  monthWeekdayLabels,
  weekDays,
} from '../dispatchCalendar';

export interface EffectivePlacement {
  meta: DispatchCardMeta;
  assigneeId: number;
  scheduledStartAt: Date;
  isTentative: boolean;
  isPastDue: boolean;
}

const dayKey = (d: Date): string =>
  `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;

export const slotId = (lane: number, day: Date, hour: number): string =>
  `slot|day:${dayKey(day)}|hour:${hour}|lane:${lane}`;

export interface ParsedSlot {
  lane: number;
  dayKey: string;
  hour: number;
}

export const parseSlotId = (id: string): ParsedSlot | undefined => {
  if (!id.startsWith('slot|')) return undefined;
  const parts = id.split('|');
  const day = parts[1]?.replace('day:', '') ?? '';
  const hour = Number(parts[2]?.replace('hour:', ''));
  const lane = Number(parts[3]?.replace('lane:', ''));
  return {lane, dayKey: day, hour};
};

const clampHour = (hour: number): number =>
  Math.min(DAY_END_HOUR, Math.max(DAY_START_HOUR, hour));

const EMPTY_PLACEMENTS: EffectivePlacement[] = [];

// Offset covers the back bar, header, and chrome above the grid so hour rows
// scroll internally instead of pushing the page past the fold.
const GRID_SCROLL_HEIGHT = 'calc(100vh - 320px)';
const GRID_SCROLL_MIN_HEIGHT = 360;

export interface DispatchCalendarGridProps {
  view: DispatchView;
  anchor: Date;
  lanes: InspectionDispatchInspectorResponse[];
  placements: EffectivePlacement[];
  renderCard: (placement: EffectivePlacement) => React.ReactNode;
  onDrillDay: (day: Date) => void;
  isDragActive: boolean;
}

const DroppableCell: React.FC<{
  droppableId: string;
  placements: EffectivePlacement[];
  renderCard: (p: EffectivePlacement) => React.ReactNode;
  isDragActive: boolean;
  // At-rest background from today/weekend context; drag styles still win.
  restBackground?: string;
  style?: React.CSSProperties;
}> = ({
  droppableId,
  placements,
  renderCard,
  isDragActive,
  restBackground,
  style,
}) => (
  <Droppable droppableId={droppableId}>
    {(provided, snapshot) => {
      const background = snapshot.isDraggingOver
        ? 'var(--mantine-color-yellow-0)'
        : restBackground ??
          (isDragActive ? 'var(--mantine-color-gray-0)' : undefined);
      return (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          p={4}
          style={{
            minHeight: 56,
            transition: 'background 120ms ease, box-shadow 120ms ease',
            background,
            boxShadow: snapshot.isDraggingOver
              ? 'inset 0 0 0 2px var(--mantine-color-yellow-5)'
              : undefined,
            borderRadius: 4,
            ...style,
          }}>
          <Stack gap={4}>
            {placements.map((p, index) => (
              <Draggable
                key={p.meta.id}
                draggableId={String(p.meta.id)}
                index={index}>
                {(dragProvided, dragSnapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    style={{
                      ...dragProvided.draggableProps.style,
                      cursor: 'grab',
                      ...(dragSnapshot.isDragging
                        ? {
                            boxShadow: 'var(--mantine-shadow-lg)',
                            opacity: 0.95,
                          }
                        : {}),
                    }}>
                    {renderCard(p)}
                  </div>
                )}
              </Draggable>
            ))}
          </Stack>
          {provided.placeholder}
        </Box>
      );
    }}
  </Droppable>
);

const headerCellStyle: React.CSSProperties = {
  padding: 8,
  fontWeight: 600,
  fontSize: 12,
  borderBottom: '1px solid var(--mantine-color-gray-3)',
};

const stickyLeftStyle: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: 'var(--mantine-color-body)',
};

// Grid lines for droppable body cells (borderLeft skipped on first column).
const cellBorderStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--mantine-color-gray-2)',
  borderLeft: '1px solid var(--mantine-color-gray-2)',
};

// First-column labels: gray-2 bottom border, sticky to the left edge.
const rowLabelStyle: React.CSSProperties = {
  padding: 8,
  fontSize: 12,
  borderBottom: '1px solid var(--mantine-color-gray-2)',
  ...stickyLeftStyle,
};

const todayDayKey = (): string => {
  const now = new Date();
  return dayKey(
    new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  );
};

export const DispatchCalendarGrid: React.FC<DispatchCalendarGridProps> = ({
  view,
  anchor,
  lanes,
  placements,
  renderCard,
  onDrillDay,
  isDragActive,
}) => {
  const todayKey = todayDayKey();
  // Narrow viewports get a permanently visible scrollbar: the board is a
  // deliberate two-axis scroller and the overlay bars are undiscoverable.
  const isCompact = useMediaQuery('(max-width: 61.99em)') ?? false;
  const scrollType = isCompact ? 'always' : 'auto';
  const laneMinWidth = isCompact ? 140 : 160;
  // One bucketing pass keeps per-cell lookups O(1) under drag re-renders.
  // Day keys on lane|day|hour; week on lane|day; month on day (lane-agnostic).
  const buckets = useMemo(() => {
    const map = new Map<string, EffectivePlacement[]>();
    const add = (key: string, p: EffectivePlacement) => {
      const arr = map.get(key);
      if (arr) arr.push(p);
      else map.set(key, [p]);
    };
    for (const p of placements) {
      const dk = dayKey(p.scheduledStartAt);
      if (view === 'day') {
        add(
          `${p.assigneeId}|${dk}|${clampHour(
            p.scheduledStartAt.getUTCHours()
          )}`,
          p
        );
      } else if (view === 'week') {
        add(`${p.assigneeId}|${dk}`, p);
      } else {
        add(dk, p);
      }
    }
    return map;
  }, [placements, view]);

  if (view === 'day') {
    const anchorKey = dayKey(anchor);
    return (
      <Paper withBorder>
        <ScrollArea
          type={scrollType}
          h={GRID_SCROLL_HEIGHT}
          mih={GRID_SCROLL_MIN_HEIGHT}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: `70px repeat(${lanes.length}, minmax(${laneMinWidth}px, 1fr))`,
            }}>
            <div style={{...headerCellStyle, ...stickyLeftStyle}} />
            {lanes.map(lane => (
              <div key={lane.userId} style={headerCellStyle}>
                {lane.name}
              </div>
            ))}
            {dayHours().map(hour => (
              <React.Fragment key={hour}>
                <div style={{...rowLabelStyle, fontWeight: 400}}>
                  {String(hour).padStart(2, '0')}:00
                </div>
                {lanes.map(lane => (
                  <DroppableCell
                    key={`${lane.userId}-${hour}`}
                    droppableId={slotId(lane.userId, anchor, hour)}
                    placements={
                      buckets.get(`${lane.userId}|${anchorKey}|${hour}`) ??
                      EMPTY_PLACEMENTS
                    }
                    renderCard={renderCard}
                    isDragActive={isDragActive}
                    style={cellBorderStyle}
                  />
                ))}
              </React.Fragment>
            ))}
          </Box>
        </ScrollArea>
      </Paper>
    );
  }

  if (view === 'week') {
    const days = weekDays(anchor);
    return (
      <Paper withBorder>
        <ScrollArea
          type={scrollType}
          h={GRID_SCROLL_HEIGHT}
          mih={GRID_SCROLL_MIN_HEIGHT}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: `140px repeat(7, minmax(130px, 1fr))`,
            }}>
            <div style={{...headerCellStyle, ...stickyLeftStyle}} />
            {days.map((day, i) => {
              const isToday = dayKey(day) === todayKey;
              const isWeekend = i >= 5;
              return (
                <div
                  key={day.toISOString()}
                  style={{
                    ...headerCellStyle,
                    ...(isToday
                      ? {
                          fontWeight: 700,
                          color: 'var(--mantine-color-yellow-8)',
                        }
                      : isWeekend
                      ? {color: 'var(--mantine-color-gray-6)'}
                      : {}),
                  }}>
                  {day.toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </div>
              );
            })}
            {lanes.map(lane => (
              <React.Fragment key={lane.userId}>
                <div style={{...rowLabelStyle, fontWeight: 500}}>
                  {lane.name}
                </div>
                {days.map((day, i) => {
                  const isToday = dayKey(day) === todayKey;
                  const isWeekend = i >= 5;
                  const restBackground = isToday
                    ? 'var(--mantine-color-yellow-0)'
                    : isWeekend
                    ? 'var(--mantine-color-gray-0)'
                    : undefined;
                  return (
                    <DroppableCell
                      key={`${lane.userId}-${day.toISOString()}`}
                      droppableId={slotId(lane.userId, day, WEEK_DEFAULT_HOUR)}
                      placements={
                        buckets.get(`${lane.userId}|${dayKey(day)}`) ??
                        EMPTY_PLACEMENTS
                      }
                      renderCard={renderCard}
                      isDragActive={isDragActive}
                      restBackground={restBackground}
                      style={cellBorderStyle}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </ScrollArea>
      </Paper>
    );
  }

  // Month: read-only overview; a day cell drills into Day view.
  const cells = monthCalendarCells(anchor);
  return (
    <Paper withBorder p="sm">
      <Text size="xs" c="dimmed" mb="xs">
        Overview — committed inspections per day. Click a day to dispatch it.
      </Text>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 6,
        }}>
        {monthWeekdayLabels().map(label => (
          <div key={label} style={{...headerCellStyle, textAlign: 'center'}}>
            {label}
          </div>
        ))}
        {cells.map((day, index) => {
          if (!day) return <Box key={`blank-${index}`} />;
          const count = (buckets.get(dayKey(day)) ?? EMPTY_PLACEMENTS).length;
          const isToday = dayKey(day) === todayKey;
          const dayLabel = day.toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            timeZone: 'UTC',
          });
          return (
            <UnstyledButton
              key={day.toISOString()}
              onClick={() => onDrillDay(day)}
              aria-label={`${dayLabel}, ${count} inspection${
                count === 1 ? '' : 's'
              }`}
              style={{display: 'block'}}>
              <Paper
                withBorder
                p="xs"
                mih={64}
                style={{
                  transition: 'box-shadow 100ms ease',
                  boxShadow: isToday
                    ? 'inset 0 0 0 2px var(--mantine-color-yellow-6)'
                    : undefined,
                }}>
                <Group justify="space-between">
                  <Text size="sm" fw={600}>
                    {day.getUTCDate()}
                  </Text>
                  {count > 0 && <Pill text={String(count)} color="gray" />}
                </Group>
              </Paper>
            </UnstyledButton>
          );
        })}
      </Box>
    </Paper>
  );
};
